import {
  Alert,
  Box,
  Button,
  Dialog,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close, OpenInNew, Refresh } from "@mui/icons-material";
import { useEffect, useState } from "react";

const FieldIframeDialog = ({ open, onClose, url, fieldName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setHasLoadError(false);
    }
  }, [open, url]);

  const handleRetry = () => {
    setIsLoading(true);
    setHasLoadError(false);
    setIframeKey((currentKey) => currentKey + 1);
  };

  const handleLoad = () => {
    setIsLoading(false);
    setHasLoadError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasLoadError(true);
  };

  return (
    <Dialog
      open={open && Boolean(url)}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      aria-labelledby="field-iframe-dialog-title"
      slotProps={{
        paper: {
          id: "field-iframe-dialog",
          sx: {
            width: isMobile ? "100%" : "min(1400px, 94vw)",
            height: isMobile ? "100%" : "min(900px, 90vh)",
            m: isMobile ? 0 : 2,
            borderRadius: isMobile ? 0 : "4px",
            overflow: "hidden",
          },
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        px={{ xs: 2, sm: 3 }}
        py={1.5}
        borderBottom="1px solid"
        borderColor="divider"
      >
        <Box minWidth={0}>
          <Typography
            id="field-iframe-dialog-title"
            fontWeight="bold"
            fontSize={{ xs: "16px", sm: "20px" }}
            noWrap
          >
            Dettaglio mappa satellitare
          </Typography>
          {fieldName && (
            <Typography fontSize="12px" color="text.secondary" noWrap>
              {fieldName}
            </Typography>
          )}
        </Box>

        <Stack direction="row" alignItems="center" gap={0.5}>
          {!isMobile && (
            <Button
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNew />}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              Apri in una nuova scheda
            </Button>
          )}
          {isMobile && (
            <Tooltip title="Apri in una nuova scheda">
              <IconButton
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Apri la mappa satellitare in una nuova scheda"
              >
                <OpenInNew sx={{ width: 20, height: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            aria-label="Chiudi la mappa satellitare"
            onClick={onClose}
          >
            <Close sx={{ width: 20, height: 20 }} />
          </IconButton>
        </Stack>
      </Stack>

      <Box
        position="relative"
        flex={1}
        minHeight={0}
        bgcolor={theme.palette.custom.backgroundColor}
      >
        {isLoading && (
          <Skeleton
            variant="rectangular"
            animation="wave"
            width="100%"
            height="100%"
            sx={{ position: "absolute", inset: 0, zIndex: 1 }}
          />
        )}

        {hasLoadError && (
          <Stack
            position="absolute"
            inset={0}
            zIndex={2}
            alignItems="center"
            justifyContent="center"
            spacing={2}
            p={3}
          >
            <Alert severity="error" sx={{ maxWidth: 560 }}>
              Non è stato possibile caricare la mappa satellitare in questa
              finestra.
            </Alert>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleRetry}
              sx={{ textTransform: "none", borderRadius: "2px" }}
            >
              Riprova
            </Button>
          </Stack>
        )}

        <Box
          key={iframeKey}
          component="iframe"
          src={url}
          title={`Dettaglio mappa satellitare${fieldName ? ` - ${fieldName}` : ""}`}
          onLoad={handleLoad}
          onError={handleError}
          allow="autoplay; clipboard-read; clipboard-write; fullscreen; geolocation; xr-spatial-tracking"
          sandbox="allow-downloads allow-forms allow-modals allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          sx={{
            display: hasLoadError ? "none" : "block",
            width: "100%",
            height: "100%",
            border: 0,
            bgcolor: "background.paper",
          }}
        />
      </Box>
    </Dialog>
  );
};

export default FieldIframeDialog;
